// Armwrestling Workout Generator — pure, deterministic plan rules.
//
// No React, no i18n, no DOM: this module only decides WHICH exercises from the
// site's existing exercise library (src/content/exercises/data.generated.ts,
// synced from the ArmProgress app) go where, and with what dose. The UI layer
// resolves ids to localized names and exercise-page links. Same inputs (+ the
// same swap offsets) always produce the same plan, so a share URL reproduces it.
//
// Unit tests: tests/workoutGenerator.test.ts (`npm test`, Node's built-in runner).
import { EXERCISES } from '../content/exercises/data.generated.ts';
import type { ExerciseRecord, Lang } from '../content/exercises/data.generated.ts';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export const LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export const DAY_OPTIONS = [2, 3, 4, 5] as const;
export const MINUTE_OPTIONS = [30, 45, 60, 90] as const;
export const FOCUSES = ['toproll', 'hook', 'press', 'allround'] as const;
export const ARMS = ['right', 'left', 'both'] as const;

export type Level = (typeof LEVELS)[number];
export type Days = (typeof DAY_OPTIONS)[number];
export type Minutes = (typeof MINUTE_OPTIONS)[number];
export type Focus = (typeof FOCUSES)[number];
export type Arm = (typeof ARMS)[number];

// User-facing equipment checkboxes. Each maps onto one or more of the exercise
// library's EquipmentType keys (src/content/exercises/equipmentLabels.ts).
// Bodyweight/home ("none" + rice bucket) is always included.
export const EQUIPMENT_OPTIONS = [
  'dumbbell',
  'band',
  'cable',
  'wristRoller',
  'loadingPin',
  'strap',
  'table',
  'barbell',
  'kettlebell',
  'sledgehammer',
  'riser',
  'gripper',
  'pullupBar',
] as const;
export type EquipmentOption = (typeof EQUIPMENT_OPTIONS)[number];

export const EQUIPMENT_OPTION_KEYS: Record<EquipmentOption, string[]> = {
  dumbbell: ['dumbbell'],
  band: ['band'],
  cable: ['cable'],
  wristRoller: ['wristRoller'],
  loadingPin: ['multispinner', 'chain'],
  strap: ['strap'],
  table: ['table'],
  barbell: ['barbell', 'ezBar'],
  kettlebell: ['kettlebell'],
  sledgehammer: ['sledgehammer'],
  riser: ['riser'],
  gripper: ['gripper'],
  pullupBar: ['pullupBar'],
};
const ALWAYS_AVAILABLE = ['none', 'riceBucket'];

export interface Inputs {
  level: Level;
  days: Days;
  minutes: Minutes;
  equipment: EquipmentOption[];
  focus: Focus;
  arm: Arm;
}

export const DEFAULT_INPUTS: Inputs = {
  level: 'beginner',
  days: 3,
  minutes: 45,
  equipment: ['dumbbell', 'band'],
  focus: 'allround',
  arm: 'right',
};

// Swap offsets from the "shuffle alternatives" button, keyed by slot key
// ("<sessionIndex>-<slotIndex>").
export type Swaps = Record<string, number>;

// ---------------------------------------------------------------------------
// Movement patterns — every library exercise mapped explicitly, so a new
// exercise synced from the app can't silently land in the wrong slot (a unit
// test fails until it's mapped here).
// ---------------------------------------------------------------------------

export const PATTERNS = [
  'warmup',
  'pronation',
  'cupping',
  'rising',
  'supination',
  'sidePressure',
  'backPressure',
  'elbowFlexion',
  'extensors',
  'fingers',
] as const;
export type Pattern = (typeof PATTERNS)[number];

export const PATTERN_BY_ID: Record<string, Pattern> = {
  // warm-up
  riceBucket: 'warmup',
  bandFingerExtensions: 'warmup',
  wristCircles: 'warmup',
  forearmStretches: 'warmup',
  // pronation (toproll / hand control)
  pronationCurls: 'pronation',
  pronationHold: 'pronation',
  larrattPronationPulls: 'pronation',
  bandPronationPulls: 'pronation',
  bandHandlePronationCurls: 'pronation',
  explosivePronationLifts: 'pronation',
  chainHandlePronationLifts: 'pronation',
  pronationPulls45: 'pronation',
  pronationPulls110: 'pronation',
  staticPronationHold110: 'pronation',
  staticPronationHold45: 'pronation',
  staticPronationBandHold: 'pronation',
  // cupping / wrist flexion
  wristCurls: 'cupping',
  cupping: 'cupping',
  cableWristCurls: 'cupping',
  cableWristCurls45: 'cupping',
  bandHandleWristCurls: 'cupping',
  wristCurlsWinningPosition: 'cupping',
  singleBarWristCurls: 'cupping',
  offsetBarWristCurls: 'cupping',
  generalWristStrengthDrill: 'cupping',
  multispinnerWristFingerDrill: 'cupping',
  // rising / radial deviation
  rising: 'rising',
  beltOverFingersRiserPulls: 'rising',
  riserPulls110: 'rising',
  riserDynamicPulls: 'rising',
  tableRiserLift: 'rising',
  riserOverFingers45: 'rising',
  riserPullOverFingers45: 'rising',
  staticRiserHold: 'rising',
  staticRiserOverFingers110: 'rising',
  staticRiserOverFingers: 'rising',
  // supination (hook)
  supinationCurls: 'supination',
  supinationPull: 'supination',
  supinationPulls45: 'supination',
  bandSupinationPulls: 'supination',
  bandHandleSupinationCurls: 'supination',
  staticSupinationHold110: 'supination',
  staticSupinationHold45: 'supination',
  staticSupinationHold: 'supination',
  tableSupinationLifts: 'supination',
  // side pressure
  sidePressureDrills: 'sidePressure',
  pronatedSidePressure: 'sidePressure',
  supinatedSidePressure: 'sidePressure',
  wristCurlsWithSidePressure: 'sidePressure',
  staticBarWristCurlsSidePressure: 'sidePressure',
  // back pressure (pulling toward your body)
  hookPulls45: 'backPressure',
  wristEngagedPullUps: 'backPressure',
  // elbow flexion (accessory)
  hammerCurls: 'elbowFlexion',
  bicepCurls: 'elbowFlexion',
  ezBarHammerCurls: 'elbowFlexion',
  reverseGripBicepCurls: 'elbowFlexion',
  scottBenchBicepCurls: 'elbowFlexion',
  // wrist extensors (accessory / tendon balance)
  reverseWristCurls: 'extensors',
  tableReverseWristCurls: 'extensors',
  staticReverseWristCurlHold: 'extensors',
  kettlebellReverseGripHold: 'extensors',
  // fingers & grip (accessory)
  fingerHold: 'fingers',
  larrattsLiftOverFingers: 'fingers',
  larrattsLift: 'fingers',
  twoByTwoHandleFingerHold: 'fingers',
  kettlebellStaticHold: 'fingers',
};

// Equipment requirements. Default rule: the library's `equipmentKeys` are
// ALTERNATIVES ("dumbbell or barbell"), "none" means always doable, and a
// table is optional (a bench or thigh works) unless the exercise is
// table-specific. These exercises need ALL of a set (anyOf → allOf):
const REQUIREMENT_OVERRIDES: Record<string, string[][]> = {
  tableSupinationLifts: [['table', 'dumbbell']],
  tableReverseWristCurls: [['table', 'dumbbell']],
  tableRiserLift: [['table', 'riser']],
  wristCurlsWinningPosition: [['dumbbell']],
  wristCurlsWithSidePressure: [['dumbbell']],
  staticBarWristCurlsSidePressure: [['barbell', 'table']],
  beltOverFingersRiserPulls: [['riser', 'strap']],
  riserOverFingers45: [['riser', 'strap']],
  riserPullOverFingers45: [['riser', 'strap']],
  staticRiserOverFingers110: [['riser', 'strap']],
  staticRiserOverFingers: [['riser', 'strap']],
  multispinnerWristFingerDrill: [['multispinner', 'cable'], ['multispinner', 'band']],
};

export function requirementsFor(ex: Pick<ExerciseRecord, 'id' | 'equipmentKeys'>): string[][] {
  const o = REQUIREMENT_OVERRIDES[ex.id];
  if (o) return o;
  if (ex.equipmentKeys.includes('none')) return [[]];
  const alts = ex.equipmentKeys.filter((k) => k !== 'table');
  return alts.length ? alts.map((k) => [k]) : [['table']];
}

export function availableKeys(equipment: EquipmentOption[]): Set<string> {
  const s = new Set<string>(ALWAYS_AVAILABLE);
  for (const opt of equipment) for (const k of EQUIPMENT_OPTION_KEYS[opt] || []) s.add(k);
  return s;
}

export function isDoable(ex: Pick<ExerciseRecord, 'id' | 'equipmentKeys'>, keys: Set<string>): boolean {
  return requirementsFor(ex).some((set) => set.every((k) => keys.has(k)));
}

// Non-library equipment items that still have their own page on the site
// (src/content/equipment/data.ts) — used as an accessory when the user owns one.
export const EQUIPMENT_ITEMS = {
  wristRoller: { id: 'wristRoller', slug: 'wrist-roller', requires: 'wristRoller', pattern: 'extensors' as Pattern },
  table: { id: 'armWrestlingTable', slug: 'arm-wrestling-table', requires: 'table' },
};

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

export type ItemRef =
  | { kind: 'exercise'; id: string; slug: string }
  | { kind: 'equipment'; id: string; slug: string };

export type Role = 'warmup' | 'primary' | 'secondary' | 'accessory' | 'light' | 'table';

export type Range = [number, number];

export interface PlanItem {
  /** Stable key for swaps; null for items that can't be shuffled (warm-ups, table). */
  slotKey: string | null;
  pattern: Pattern | 'table';
  role: Role;
  ref: ItemRef;
  /** Number of alternatives (including this one) available for the slot. */
  options: number;
  isStatic: boolean;
  /** Warm-ups use the library's own localized dose text instead. */
  useLibraryDose: boolean;
  sets: number;
  reps: Range | null;
  holdSec: Range | null;
  restSec: Range | null;
  /** Table practice only. */
  durationMin?: Range;
}

export interface Session {
  index: number;
  light: boolean;
  focusPatterns: Pattern[];
  warmup: PlanItem[];
  table: PlanItem | null;
  main: PlanItem[];
  accessory: PlanItem[];
}

export interface PlanDay {
  /** 0 = Monday … 6 = Sunday. */
  weekday: number;
  session: Session | null;
}

export interface Plan {
  inputs: Inputs;
  days: PlanDay[];
  /** Main patterns the focus wanted but the selected equipment can't cover. */
  skippedPatterns: Pattern[];
  /** True when the selected equipment leaves almost nothing to train. */
  lowEquipment: boolean;
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

// Monday-based training-day layouts with recovery days in between.
export const WEEK_LAYOUT: Record<Days, number[]> = {
  2: [0, 3],
  3: [0, 2, 4],
  4: [0, 1, 3, 4],
  5: [0, 1, 2, 4, 5],
};

// Focus → primary patterns (trained most often, heaviest slot) + secondaries.
const FOCUS_PATTERNS: Record<Focus, { primary: Pattern[]; secondary: Pattern[] }> = {
  toproll: { primary: ['pronation', 'rising'], secondary: ['backPressure', 'cupping', 'sidePressure', 'supination'] },
  hook: { primary: ['supination', 'cupping'], secondary: ['sidePressure', 'backPressure', 'pronation', 'rising'] },
  press: { primary: ['sidePressure', 'cupping'], secondary: ['supination', 'pronation', 'backPressure', 'rising'] },
  allround: { primary: [], secondary: ['pronation', 'cupping', 'rising', 'sidePressure', 'supination', 'backPressure'] },
};

const ACCESSORY_ORDER: Record<Level, Pattern[]> = {
  // Beginners: extensor + finger balance work first (tendon conditioning).
  beginner: ['extensors', 'fingers', 'elbowFlexion'],
  intermediate: ['elbowFlexion', 'fingers', 'extensors'],
  advanced: ['elbowFlexion', 'extensors', 'fingers'],
};

// Slot counts by level and session length. Beginners are capped low on purpose.
const SLOT_COUNTS: Record<Level, Record<Minutes, { warmup: number; main: number; accessory: number }>> = {
  beginner: {
    30: { warmup: 2, main: 2, accessory: 1 },
    45: { warmup: 3, main: 2, accessory: 2 },
    60: { warmup: 3, main: 3, accessory: 2 },
    90: { warmup: 3, main: 3, accessory: 2 },
  },
  intermediate: {
    30: { warmup: 2, main: 2, accessory: 1 },
    45: { warmup: 3, main: 3, accessory: 1 },
    60: { warmup: 3, main: 4, accessory: 2 },
    90: { warmup: 3, main: 5, accessory: 2 },
  },
  advanced: {
    30: { warmup: 2, main: 2, accessory: 1 },
    45: { warmup: 3, main: 3, accessory: 1 },
    60: { warmup: 3, main: 4, accessory: 2 },
    90: { warmup: 3, main: 5, accessory: 2 },
  },
};

interface Dose {
  sets: number;
  reps: Range;
  holdSec: Range;
  restSec: Range;
}

// Conservative doses. Beginners: lighter, higher-rep, longer low-load holds.
export const DOSES: Record<Level, Record<'primary' | 'secondary' | 'accessory' | 'light', Dose>> = {
  beginner: {
    primary: { sets: 3, reps: [12, 15], holdSec: [15, 20], restSec: [60, 90] },
    secondary: { sets: 2, reps: [12, 15], holdSec: [15, 20], restSec: [60, 90] },
    accessory: { sets: 2, reps: [12, 15], holdSec: [15, 20], restSec: [60, 60] },
    light: { sets: 2, reps: [15, 20], holdSec: [15, 20], restSec: [60, 60] },
  },
  intermediate: {
    primary: { sets: 4, reps: [8, 10], holdSec: [10, 15], restSec: [90, 120] },
    secondary: { sets: 3, reps: [10, 12], holdSec: [10, 15], restSec: [90, 90] },
    accessory: { sets: 2, reps: [12, 15], holdSec: [15, 20], restSec: [60, 90] },
    light: { sets: 2, reps: [15, 20], holdSec: [15, 20], restSec: [60, 60] },
  },
  advanced: {
    primary: { sets: 5, reps: [5, 8], holdSec: [6, 10], restSec: [120, 180] },
    secondary: { sets: 3, reps: [8, 10], holdSec: [10, 15], restSec: [90, 120] },
    accessory: { sets: 3, reps: [10, 15], holdSec: [15, 20], restSec: [60, 90] },
    light: { sets: 3, reps: [12, 15], holdSec: [15, 20], restSec: [60, 90] },
  },
};

// Warm-up preference: equipment-free first, rice bucket last (not everyone has one).
const WARMUP_ORDER = ['wristCircles', 'forearmStretches', 'bandFingerExtensions', 'riceBucket'];

const LEVEL_RANK: Record<ExerciseRecord['difficulty'], number> = { beginner: 0, intermediate: 1, advanced: 2 };

/**
 * Candidate exercises for a pattern: doable with the equipment, translated for
 * `lang` (so every link resolves to a real page), and level-appropriate.
 * Beginners never get advanced exercises and only get intermediate ones when no
 * beginner option exists; intermediates never get advanced ones. Sorted with
 * the best level match first, then by library order (stable, deterministic).
 */
export function candidatesFor(pattern: Pattern, inputs: Pick<Inputs, 'level' | 'equipment'>, lang: Lang): ItemRef[] {
  const keys = availableKeys(inputs.equipment);
  const lvl = LEVEL_RANK[inputs.level];
  const pool = EXERCISES.filter(
    (e) => PATTERN_BY_ID[e.id] === pattern && e.translations[lang] && isDoable(e, keys) && LEVEL_RANK[e.difficulty] <= lvl,
  );
  let chosen = pool;
  if (inputs.level === 'beginner') {
    const easy = pool.filter((e) => e.difficulty === 'beginner');
    if (easy.length) chosen = easy;
  }
  // Preferred difficulty: exact level match, then one step below.
  const target = inputs.level === 'advanced' ? 1.5 : lvl;
  const ranked: ItemRef[] = chosen
    .map((e, i) => ({ e, i, d: Math.abs(LEVEL_RANK[e.difficulty] - target) }))
    .sort((a, b) => a.d - b.d || a.i - b.i)
    .map(({ e }) => ({ kind: 'exercise' as const, id: e.id, slug: e.slug }));
  const wr = EQUIPMENT_ITEMS.wristRoller;
  if (pattern === wr.pattern && keys.has(wr.requires)) {
    ranked.push({ kind: 'equipment', id: wr.id, slug: wr.slug });
  }
  return ranked;
}

const rotate = <T>(arr: T[], n: number): T[] => {
  if (!arr.length) return arr;
  const k = ((n % arr.length) + arr.length) % arr.length;
  return [...arr.slice(k), ...arr.slice(0, k)];
};

function staticOf(ref: ItemRef): boolean {
  if (ref.kind !== 'exercise') return false;
  return EXERCISES.find((e) => e.id === ref.id)?.category === 'static';
}

function makeItem(
  slotKey: string | null,
  pattern: Pattern,
  role: 'primary' | 'secondary' | 'accessory' | 'light',
  candidates: ItemRef[],
  pick: number,
  level: Level,
): PlanItem {
  const ref = candidates[((pick % candidates.length) + candidates.length) % candidates.length];
  const dose = DOSES[level][role];
  const isStatic = staticOf(ref);
  return {
    slotKey,
    pattern,
    role,
    ref,
    options: candidates.length,
    isStatic,
    useLibraryDose: false,
    sets: dose.sets,
    reps: isStatic ? null : dose.reps,
    holdSec: isStatic ? dose.holdSec : null,
    restSec: dose.restSec,
  };
}

/** Main-pattern order for one session (before equipment filtering). */
export function sessionPatternOrder(focus: Focus, days: Days, sessionIndex: number, mainCount: number): Pattern[] {
  const { primary, secondary } = FOCUS_PATTERNS[focus];
  if (!primary.length) return rotate(secondary, sessionIndex * mainCount);
  const lead = primary[sessionIndex % primary.length];
  const other = primary.filter((p) => p !== lead);
  // ≤3 days: both primaries every session. 4–5 days (back-to-back days): only
  // the lead primary, so consecutive sessions load different tissues.
  const head = days <= 3 ? [lead, ...other] : [lead];
  const tail = days <= 3 ? secondary : [...secondary, ...other];
  return [...head, ...rotate(tail, sessionIndex * Math.max(1, mainCount - head.length))];
}

export function generatePlan(inputs: Inputs, lang: Lang = 'en', swaps: Swaps = {}): Plan {
  const { level, days, minutes, focus, arm } = inputs;
  const layout = WEEK_LAYOUT[days];
  const counts = SLOT_COUNTS[level][minutes];
  const keys = availableKeys(inputs.equipment);
  const cache = new Map<Pattern, ItemRef[]>();
  const cand = (p: Pattern) => {
    if (!cache.has(p)) cache.set(p, candidatesFor(p, inputs, lang));
    return cache.get(p)!;
  };

  const wanted = [...FOCUS_PATTERNS[focus].primary, ...FOCUS_PATTERNS[focus].secondary];
  const skippedPatterns = wanted.filter((p) => cand(p).length === 0);

  const sessions: Session[] = layout.map((_, s) => {
    // Beginners training 4–5 days: sessions 4 and 5 are light technique/tendon days.
    const light = level === 'beginner' && s >= 3;
    // More sessions per week → fewer main movements per session; training both
    // arms roughly doubles the work, so it costs one main slot too.
    const trimmed = counts.main - (days >= 4 ? 1 : 0) - (arm === 'both' ? 1 : 0);
    const mainCount = light ? 2 : Math.max(2, trimmed);
    // Training both arms alternately takes longer; trim accessories on short sessions.
    const accCount = Math.max(0, counts.accessory - (arm === 'both' && minutes <= 45 ? 1 : 0));

    let slot = 0;
    const main: PlanItem[] = [];
    for (const p of sessionPatternOrder(focus, days, s, mainCount)) {
      if (main.length >= mainCount) break;
      const c = cand(p);
      if (!c.length) continue;
      const key = `${s}-${slot++}`;
      const role = light ? 'light' : main.length === 0 ? 'primary' : 'secondary';
      main.push(makeItem(key, p, role, c, s + (swaps[key] || 0), level));
    }

    const accessory: PlanItem[] = [];
    for (const p of rotate(ACCESSORY_ORDER[level], s)) {
      if (accessory.length >= accCount) break;
      const c = cand(p);
      if (!c.length) continue;
      const key = `${s}-${slot++}`;
      accessory.push(makeItem(key, p, 'accessory', c, s + (swaps[key] || 0), level));
    }

    const warmPool = cand('warmup');
    const warmRefs = WARMUP_ORDER.map((id) => warmPool.find((r) => r.id === id))
      .filter((r): r is ItemRef => !!r)
      .slice(0, light ? 3 : counts.warmup);
    const warmup: PlanItem[] = warmRefs.map((ref) => ({
      slotKey: null,
      pattern: 'warmup',
      role: 'warmup',
      ref,
      options: 1,
      isStatic: false,
      useLibraryDose: true,
      sets: 0,
      reps: null,
      holdSec: null,
      restSec: null,
    }));

    // Table practice: one session a week for beginners (technique only), two
    // for everyone else, never on a light day, and only with ≥45 min.
    const tableSessions = level === 'beginner' ? [0] : [0, Math.min(2, layout.length - 1)];
    const table: PlanItem | null =
      keys.has('table') && !light && minutes >= 45 && tableSessions.includes(s)
        ? {
            slotKey: null,
            pattern: 'table',
            role: 'table',
            ref: { kind: 'equipment', id: EQUIPMENT_ITEMS.table.id, slug: EQUIPMENT_ITEMS.table.slug },
            options: 1,
            isStatic: false,
            useLibraryDose: false,
            sets: 0,
            reps: null,
            holdSec: null,
            restSec: null,
            durationMin: level === 'beginner' ? [10, 15] : minutes >= 90 ? [20, 30] : [10, 20],
          }
        : null;

    return {
      index: s,
      light,
      focusPatterns: main.slice(0, 2).map((m) => m.pattern as Pattern),
      warmup,
      table,
      main,
      accessory,
    };
  });

  const days7: PlanDay[] = Array.from({ length: 7 }, (_, weekday) => {
    const idx = layout.indexOf(weekday);
    return { weekday, session: idx === -1 ? null : sessions[idx] };
  });

  const trainable = sessions.reduce((n, s) => n + s.main.length, 0);
  return { inputs, days: days7, skippedPatterns, lowEquipment: trainable < sessions.length * 2 };
}

/** Recovery-day suggestions: gentle mobility from the library (no equipment). */
export function recoveryItems(lang: Lang): ItemRef[] {
  return ['wristCircles', 'forearmStretches']
    .map((id) => EXERCISES.find((e) => e.id === id && e.translations[lang]))
    .filter((e): e is ExerciseRecord => !!e)
    .map((e) => ({ kind: 'exercise', id: e.id, slug: e.slug }));
}

// ---------------------------------------------------------------------------
// Share-URL encoding
// ---------------------------------------------------------------------------

function pick<T extends string | number>(list: readonly T[], raw: string | null, fallback: T): T {
  if (raw == null) return fallback;
  const hit = list.find((v) => String(v) === raw);
  return hit ?? fallback;
}

export function parseInputs(params: URLSearchParams): { inputs: Inputs; swaps: Swaps } {
  const eqRaw = params.get('eq');
  const equipment =
    eqRaw == null
      ? DEFAULT_INPUTS.equipment
      : (EQUIPMENT_OPTIONS.filter((o) => eqRaw.split(',').includes(o)) as EquipmentOption[]);
  const inputs: Inputs = {
    level: pick(LEVELS, params.get('level'), DEFAULT_INPUTS.level),
    days: pick(DAY_OPTIONS, params.get('days'), DEFAULT_INPUTS.days),
    minutes: pick(MINUTE_OPTIONS, params.get('min'), DEFAULT_INPUTS.minutes),
    equipment,
    focus: pick(FOCUSES, params.get('focus'), DEFAULT_INPUTS.focus),
    arm: pick(ARMS, params.get('arm'), DEFAULT_INPUTS.arm),
  };
  const swaps: Swaps = {};
  for (const part of (params.get('sw') || '').split(',')) {
    const m = part.match(/^(\d{1,2})-(\d{1,2})-(\d{1,2})$/);
    if (m && Number(m[3]) > 0) swaps[`${Number(m[1])}-${Number(m[2])}`] = Number(m[3]);
  }
  return { inputs, swaps };
}

export function serializeInputs(inputs: Inputs, swaps: Swaps = {}): string {
  const p = new URLSearchParams();
  p.set('level', inputs.level);
  p.set('days', String(inputs.days));
  p.set('min', String(inputs.minutes));
  p.set('focus', inputs.focus);
  p.set('arm', inputs.arm);
  // Keep checkbox order canonical so equal plans give equal URLs.
  p.set('eq', EQUIPMENT_OPTIONS.filter((o) => inputs.equipment.includes(o)).join(','));
  const sw = Object.entries(swaps)
    .filter(([, n]) => n > 0)
    .sort(([a], [b]) => a.localeCompare(b, 'en', { numeric: true }))
    .map(([k, n]) => `${k}-${n}`)
    .join(',');
  if (sw) p.set('sw', sw);
  return p.toString().replace(/%2C/g, ',');
}
